import React, { useEffect, useState } from "react";
import { UserProgress } from "../../types/user.types";
import { UsersAPI } from "../../APIs/users.api";
import { useToast } from "../../components/toast";
import { useNavigate } from "react-router-dom";
import { HomeHeader } from "../../components/home-header";
import { Button } from "../../components/button";
import {
  BanknotesIcon,
  CalendarIcon,
  ClockIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { Loading } from "../../components/loading";
import { ErrorMessage } from "../../components/error-message";

interface RegistrationStep {
  id: number;
  title: string;
  description: string;
  isComplete: boolean;
  isWarning?: boolean;
}

export const PrimaryUserHome: React.FC = () => {
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();

  useEffect(() => {
    const fetchUserProgress = async () => {
      setIsLoading(true);
      clearError();
      try {
        const response = await UsersAPI.getUserProgress();
        if (response.statusCode === 200) {
          setUserProgress(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError(
          "Error al obtener el progreso de usuario, intente nuevamente",
          { persist: true }
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserProgress();
  }, [addToast, handleError, clearError]);

  useEffect(() => {
    if (userProgress) {
      setSteps([
        {
          id: 1,
          title: "Activa tu cuenta",
          description: userProgress.is_activated
            ? "Tu cuenta ha sido activada correctamente"
            : "Activa tu cuenta para continuar con el registro",
          isComplete: userProgress.is_activated ?? false,
        },
        {
          id: 2,
          title: "Crea tu equipo",
          description: userProgress.has_team
            ? "Tu equipo ha sido creado correctamente"
            : "",
          isComplete: userProgress.has_team ?? false,
        },
        {
          id: 3,
          title: "Registra tu embarcación",
          description: userProgress.has_boat
            ? "Tu embarcación ha sido registrada correctamente"
            : "",
          isComplete: userProgress.has_boat ?? false,
        },
        {
          id: 4,
          title: userProgress.has_team
            ? `Agrega miembros a tu equipo (${
                userProgress.members_count ?? 0
              }/3)`
            : "Agrega miembros a tu equipo",
          description:
            userProgress.members_count === 3 && userProgress.has_helmsman
              ? "Tu equipo está completo con todos los miembros requeridos"
              : "Tu equipo necesita 3 miembros y uno de los miembros debe ser el timonel",
          isComplete:
            userProgress.members_count === 3 && userProgress.has_helmsman,
          isWarning:
            ((userProgress.members_count ?? 0) < 3 ||
              !userProgress.has_helmsman) &&
            userProgress.has_team,
        },
        {
          id: 5,
          title: "Paga la inscripción",
          description: userProgress.is_paid
            ? "La inscripción ha sido pagada"
            : !userProgress.has_team
            ? ``
            : ``,
          isComplete: userProgress.is_paid ?? false,
        },
      ]);
    }
  }, [userProgress]);

  const [steps, setSteps] = useState<RegistrationStep[]>([]);
  const [showMembersPanel, setShowMembersPanel] = useState(true);
  const completedSteps = steps.filter((step) => step.isComplete).length;

  const showCompletionBanner =
    (userProgress?.is_activated ?? false) &&
    (userProgress?.has_team ?? false) &&
    (userProgress?.has_boat ?? false) &&
    (userProgress?.members_count ?? 0) === 3 &&
    (userProgress?.has_helmsman ?? false) &&
    (userProgress?.is_paid ?? false);

  const handleAccountActivation = () => {
    navigate("/dashboard/activate-account");
  };

  const handleTeamCreation = () => {
    navigate("/dashboard/create-team");
  };

  const handleBoatRegistration = () => {
    navigate("/dashboard/my-boat#create");
  };

  const handleAddTeamMember = () => {
    navigate("/dashboard/my-members#create");
  };

  const handlePayment = () => {
    navigate("/dashboard/competitions");
  };

  const toggleMembersPanel = () => {
    setShowMembersPanel(!showMembersPanel);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const isDueDatePassed = (endDate: string) => {
    const endDateDate = new Date(endDate);
    const currentDate = new Date();
    return endDateDate < currentDate;
  };

  const getDaysRemaining = (endDate: string) => {
    const endDateDate = new Date(endDate);
    const currentDate = new Date();
    return Math.ceil(
      (new Date(endDateDate).getTime() - currentDate.getTime()) /
        (1000 * 60 * 60 * 24)
    );
  };

  const formatDate = (date: string | Date): string => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString("es-AR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <HomeHeader />
        <p className="text-gray-600 text-sm sm:text-base mt-2">
          Completa todos los pasos para finalizar tu inscripción
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      {/* Progress */}
      {isLoading ? (
        <Loading />
      ) : userProgress ? (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              Pasos para completar la inscripción
            </h2>
            <span
              className={`px-3 py-1 rounded-full font-medium text-sm w-fit ${
                completedSteps === 5
                  ? "bg-green-100 text-green-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {completedSteps}/5 Completados
              {completedSteps === 5 && " ✓"}
            </span>
          </div>

          {/* Completion Banner */}
          {showCompletionBanner ? (
            <div className="mb-6 sm:mb-8 bg-green-100 border border-green-300 rounded-lg p-4 sm:p-6 text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-green-700 mb-2">
                🎣 ¡Inscripción completa! 🎣
              </h3>
              {userProgress?.team_number && (
                <p className="text-lg sm:text-xl text-green-700">
                  Número de equipo:{" "}
                  <span className="font-bold">#{userProgress.team_number}</span>
                </p>
              )}
            </div>
          ) : null}

          {/* Registration Steps */}
          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`border rounded-lg p-3 sm:p-4 ${
                  step.isComplete
                    ? "border-green-500 bg-green-50"
                    : step.isWarning
                    ? "border-yellow-500 bg-yellow-50"
                    : "border-gray-300"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <span
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-white flex-shrink-0 text-sm sm:text-base ${
                        step.isComplete
                          ? "bg-green-500"
                          : step.isWarning
                          ? "bg-yellow-500"
                          : "bg-blue-500"
                      }`}
                    >
                      {step.isComplete ? "✓" : step.isWarning ? "!" : step.id}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base break-words">
                        {step.title}
                      </h3>

                      {step.id === 5 &&
                      !step.isComplete &&
                      userProgress?.has_team ? (
                        <div className="mt-2">
                          <p className="text-gray-600 text-sm mb-2">
                            {step.description}
                          </p>

                          {userProgress.prices &&
                          userProgress.prices.length > 0 ? (
                            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <h4 className="font-medium text-gray-700 mb-3 text-sm sm:text-base">
                                Opciones de pago disponibles
                              </h4>
                              <div className="space-y-4">
                                {userProgress.prices.map((priceOption, index) => {
                                  const daysRemaining = getDaysRemaining(
                                    priceOption.end_date
                                  );
                                  const isPassed = isDueDatePassed(
                                    priceOption.end_date
                                  );
                                  return (
                                    <div
                                      key={priceOption.id}
                                      className={`border rounded-lg p-3 ${
                                        isPassed
                                          ? "border-red-200 bg-red-50"
                                          : daysRemaining <= 3
                                          ? "border-orange-200 bg-orange-50"
                                          : daysRemaining <= 7
                                          ? "border-yellow-200 bg-yellow-50"
                                          : "border-blue-200 bg-blue-50"
                                      }`}
                                    >
                                      <div className="flex flex-col space-y-2">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                                            <div className="flex items-center min-w-0">
                                              <BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" />
                                              <span className="text-gray-600 mr-1 text-xs sm:text-sm">
                                                Precio de inscripción:
                                              </span>
                                              <span className="text-gray-800 font-medium text-xs sm:text-sm break-words">
                                                {formatCurrency(priceOption.price)}
                                              </span>
                                            </div>
                                            {priceOption.installment_count > 1 && (
                                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full w-fit">
                                                {priceOption.installment_count} cuotas
                                              </span>
                                            )}
                                          </div>
                                          
                                          {/* Payment Type Badge */}
                                          <span
                                            className={`
                                              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit
                                              ${
                                                priceOption.payment_type === "mercadopago"
                                                  ? "bg-blue-100 text-blue-800"
                                                  : priceOption.payment_type === "transfer"
                                                  ? "bg-orange-100 text-orange-800"
                                                  : "bg-green-100 text-green-800"
                                              }
                                            `}
                                          >
                                            {priceOption.payment_type === "mercadopago"
                                              ? "MercadoPago"
                                              : priceOption.payment_type === "transfer"
                                              ? "Transferencia"
                                              : "Efectivo"}
                                          </span>
                                        </div>
                                        
                                        <div className="flex items-center">
                                          <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" />
                                          <span className="text-gray-600 mr-1 text-xs sm:text-sm">
                                            Disponible desde:
                                          </span>
                                          <span className="text-gray-800 text-xs sm:text-sm break-words">
                                            {formatDate(priceOption.start_date)}
                                          </span>
                                        </div>
                                        <div className="flex items-start">
                                          <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                                            <span className="text-gray-600 text-xs sm:text-sm">
                                              Fecha límite:
                                            </span>
                                            {isPassed ? (
                                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                                <span className="text-red-600 font-medium text-xs sm:text-sm">
                                                  {formatDate(priceOption.end_date)}
                                                </span>
                                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center w-fit">
                                                  <InformationCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 mr-1" />
                                                  Plazo vencido
                                                </span>
                                              </div>
                                            ) : daysRemaining <= 3 ? (
                                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                                <span className="text-orange-600 font-medium text-xs sm:text-sm">
                                                  {formatDate(priceOption.end_date)}
                                                </span>
                                                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full flex items-center w-fit">
                                                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 mr-1" />
                                                  {daysRemaining === 0
                                                    ? "Último día"
                                                    : `${daysRemaining} día${
                                                        daysRemaining > 1 ? "s" : ""
                                                      } restante${
                                                        daysRemaining > 1 ? "s" : ""
                                                      }`}
                                                </span>
                                              </div>
                                            ) : daysRemaining <= 7 ? (
                                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                                <span className="text-yellow-700 font-medium text-xs sm:text-sm">
                                                  {formatDate(priceOption.end_date)}
                                                </span>
                                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center w-fit">
                                                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-600 mr-1" />
                                                  {daysRemaining} días restantes
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="text-gray-800 text-xs sm:text-sm">
                                                {formatDate(priceOption.end_date)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {!isPassed && (
                                        <div className="mt-3">
                                          <Button
                                            type="button"
                                            text={
                                              priceOption.installment_count > 1
                                                ? `Pagar en ${priceOption.installment_count} cuotas`
                                                : "Pagar ahora"
                                            }
                                            onClick={handlePayment}
                                            variant="primary"
                                            className="w-full sm:w-auto text-sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm">
                          {step.description}
                        </p>
                      )}

                      {/* Team Members Step */}
                      {step.id === 4 &&
                      showMembersPanel &&
                      userProgress?.has_team ? (
                        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                          <h4 className="font-medium text-gray-700 mb-3 text-sm sm:text-base">
                            Estado del equipo
                          </h4>
                          <div className="space-y-3">
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                              <span
                                className={`text-lg sm:text-xl flex-shrink-0 ${
                                  (userProgress?.members_count ?? 0) === 3
                                    ? "text-green-500"
                                    : "text-yellow-500"
                                }`}
                              >
                                {(userProgress?.members_count ?? 0) === 3
                                  ? "✓"
                                  : "⚠️"}
                              </span>
                              <span className="text-sm sm:text-base leading-relaxed">
                                {(userProgress?.members_count ?? 0) === 3
                                  ? "Tu equipo tiene los 3 miembros requeridos"
                                  : `Tu equipo necesita ${
                                      3 - (userProgress?.members_count ?? 0)
                                    } miembro${
                                      3 - (userProgress?.members_count ?? 0) > 1
                                        ? "s"
                                        : ""
                                    } más`}
                              </span>
                            </div>

                            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                              <span
                                className={`text-lg sm:text-xl flex-shrink-0 ${
                                  userProgress?.has_helmsman ?? false
                                    ? "text-green-500"
                                    : "text-yellow-500"
                                }`}
                              >
                                {userProgress?.has_helmsman ?? false ? "✓" : "⚠️"}
                              </span>
                              <span className="text-sm sm:text-base leading-relaxed">
                                {userProgress?.has_helmsman ?? false
                                  ? "Tu equipo tiene un timonel"
                                  : "Tu equipo necesita un timonel"}
                              </span>
                            </div>
                          </div>

                          {(userProgress?.members_count ?? 0) < 3 &&
                          userProgress?.has_team &&
                          userProgress?.is_activated ? (
                            <div className="mt-4">
                              <Button
                                type="button"
                                text="Agregar miembro"
                                onClick={handleAddTeamMember}
                                variant="primary"
                                className="w-full sm:w-auto text-sm"
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    {step.id === 1 &&
                    !step.isComplete &&
                    !userProgress?.is_activated ? (
                      <Button
                        type="button"
                        text="Activar cuenta"
                        onClick={handleAccountActivation}
                        variant="primary"
                        className="w-full sm:w-auto text-sm"
                      />
                    ) : null}
                    {step.id === 2 &&
                    !step.isComplete &&
                    userProgress?.is_activated &&
                    userProgress?.is_activated ? (
                      <Button
                        type="button"
                        text="Crear equipo"
                        onClick={handleTeamCreation}
                        variant="primary"
                        className="w-full sm:w-auto text-sm"
                      />
                    ) : null}
                    {step.id === 3 &&
                    !step.isComplete &&
                    userProgress?.has_team &&
                    userProgress?.is_activated ? (
                      <Button
                        type="button"
                        text="Registrar embarcación"
                        onClick={handleBoatRegistration}
                        variant="primary"
                        className="w-full sm:w-auto text-sm"
                      />
                    ) : null}
                    {step.id === 4 &&
                    userProgress?.has_team &&
                    userProgress?.is_activated ? (
                      <Button
                        type="button"
                        text={
                          showMembersPanel ? "Ocultar detalles" : "Ver detalles"
                        }
                        onClick={toggleMembersPanel}
                        variant="primary"
                        className="w-full sm:w-auto text-sm"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};